import { AlertTriangle, CloudOff } from "lucide-react";
import { resolveIcon } from "../lib/liveStatusIcons";
import { useI18n } from "../lib/i18n";
import { useOnline } from "../lib/offline";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDateMedium } from "../lib/dateFormat";

/**
 * Shared status-tile primitives + composite preview for the live-status
 * tiles that the customer sees on the home screen.
 *
 * Feb 2026 · Struktur-Fix Live-Status
 * ------------------------------------
 * Die Kachel-Auswahl ist NICHT MEHR hartcodiert: sie folgt strikt der
 * systemweiten Master-Liste (`useLiveStatusSchema`) UND der pro-Property
 * Auswahl (`property.dashboard_tile_keys`).
 *
 * - Basis-Keys (locked: last_inspection, temperature, water, heating,
 *   alarm, internet) lesen weiter aus den erstklassigen Property-Feldern
 *   (`property.temperature`, `property.temperature_severity`, …).
 * - Custom-Keys (vom Admin hinzugefügt) lesen aus
 *   `property.custom_status[key] = { value, note, severity }`.
 *
 * Truth-Source für Kunden- und Team-Vorschau: 1:1-Kopplung.
 */

export function StatusDot({ severity = "ok" }) {
  const color = severity === "attention" ? STATUS_COLORS.red : STATUS_COLORS.green;
  return <span className="w-2 h-2 rounded-full inline-block" style={{ background: color }} />;
}

export function StatusCard({ icon: Icon, label, value, note, severity, testid }) {
  const attention = severity === "attention";
  return (
    <div
      className="p-4 md:p-5 relative transition-colors"
      style={{
        background: attention ? withAlpha(STATUS_COLORS.red, 0.06) : withAlpha(BRAND_COLORS.ivory, 0.6),
        borderWidth: attention ? "1.5px" : "1px",
        borderStyle: "solid",
        borderColor: attention ? STATUS_COLORS.red : withAlpha(BRAND_COLORS.navy, 0.10),
      }}
      data-testid={testid}
      data-severity={severity || "ok"}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon
          className="w-4 h-4"
          strokeWidth={1.4}
          style={{ color: attention ? STATUS_COLORS.red : withAlpha(BRAND_COLORS.navy, 0.7) }}
        />
        <div className="label-caps" style={attention ? { color: STATUS_COLORS.red } : {}}>
          {label}
        </div>
        {attention && (
          <AlertTriangle
            className="w-3.5 h-3.5 ml-auto"
            strokeWidth={2}
            style={{ color: STATUS_COLORS.red }}
            aria-label="Attention"
          />
        )}
      </div>
      <div className="flex items-baseline gap-2">
        <StatusDot severity={severity} />
        <div
          className="text-[16px] md:text-[18px] font-display leading-none"
          style={{ color: attention ? STATUS_COLORS.red : BRAND_COLORS.navy }}
        >
          {value || "—"}
        </div>
      </div>
      {note && (
        <div
          className="text-[11.5px] mt-2 italic truncate"
          style={{ color: attention ? withAlpha(STATUS_COLORS.red, 0.75) : withAlpha(BRAND_COLORS.navy, 0.55) }}
          title={note}
        >
          {note}
        </div>
      )}
    </div>
  );
}

const LOCKED_KEYS = new Set([
  "last_inspection", "temperature", "water", "heating", "alarm", "internet",
]);

// Feb 2026 · HARDCODED-RULE — der Kunde sieht IMMER exakt diese 6 Kacheln in
// dieser Reihenfolge als 2×3-Grid. Auch wenn ein Property-Doc noch keinen
// Wert für eine Kachel hat, erscheint sie mit „—" (bzw. der Value-Renderer
// zeigt „—" bei leerem Wert an — siehe `StatusCard` unten).
const LOCKED_KEYS_ORDER = [
  "last_inspection", "temperature", "water", "heating", "alarm", "internet",
];

const LOCKED_DEFAULT_ITEMS_DE = {
  last_inspection: { key: "last_inspection", label: "Letzte Kontrolle", icon: "Clipboard" },
  temperature:     { key: "temperature",     label: "Temperatur",        icon: "Thermometer" },
  water:           { key: "water",           label: "Wasser",             icon: "Droplets" },
  heating:         { key: "heating",         label: "Heizung",            icon: "Flame" },
  alarm:           { key: "alarm",           label: "Alarm",              icon: "ShieldCheck" },
  internet:        { key: "internet",        label: "Internet / WLAN",    icon: "Wifi" },
};
const LOCKED_DEFAULT_ITEMS_EN = {
  last_inspection: { key: "last_inspection", label: "Last inspection", icon: "Clipboard" },
  temperature:     { key: "temperature",     label: "Temperature",     icon: "Thermometer" },
  water:           { key: "water",           label: "Water",            icon: "Droplets" },
  heating:         { key: "heating",         label: "Heating",          icon: "Flame" },
  alarm:           { key: "alarm",           label: "Alarm",            icon: "ShieldCheck" },
  internet:        { key: "internet",        label: "Internet / Wi-Fi", icon: "Wifi" },
};
// Fallback (deprecated name preserved for external imports if any).
const LOCKED_DEFAULT_ITEMS = LOCKED_DEFAULT_ITEMS_DE;

// Simple locale mapping for well-known base-tile labels shipped by the schema
// endpoint (which is still German-only). Keeps custom admin-defined tiles as-is.
const BASE_LABEL_EN = {
  "letzte kontrolle": "Last inspection",
  "temperatur": "Temperature",
  "wasser": "Water",
  "heizung": "Heating",
  "alarm": "Alarm",
  "internet / wlan": "Internet / Wi-Fi",
};

// Well-known DE status values that ship pre-seeded on properties. Anything
// else (custom text an admin typed) stays as-is even in EN mode.
const VALUE_EN = {
  "in ordnung": "All fine",
  "handlungsbedarf": "Attention needed",
  "erledigt": "Done",
  "aktiv": "Active",
  "scharfgestellt": "Armed",
  "deaktiviert": "Disarmed",
  "verbunden": "Connected",
  "ausfall": "Offline",
  "läuft": "Running",
  "aus": "Off",
};

// Feb 2026 · Datumsangaben immer im deutschen Format (auch bei EN-Toggle).
// Erkennt reine ISO-Daten (YYYY-MM-DD) sowie ISO-Datetime-Strings und
// wandelt sie in "TT. Mmm. JJJJ". Fällt bei ungültigen Werten auf den
// Original-String zurück.
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T[\d:.Z+-]+)?$/;
function formatIfIsoDate(value) {
  if (typeof value !== "string" || !ISO_DATE_RE.test(value)) return value;
  try {
    const src = value.length === 10 ? `${value}T00:00:00` : value;
    const d = new Date(src);
    if (isNaN(d.getTime())) return value;
    return deDateMedium(d);
  } catch { return value; }
}

function translateLabel(label, de) {
  if (de) return label;
  const key = (label || "").trim().toLowerCase().replace(/\s*\(°c\)\s*$/, "");
  return BASE_LABEL_EN[key] || label;
}
function translateValue(value, de) {
  if (!value || value === "—") return value;
  // ISO-Datum immer deutsch formatieren, egal ob DE oder EN-Toggle aktiv ist.
  const formatted = formatIfIsoDate(value);
  if (formatted !== value) return formatted;
  if (de) return value;
  const key = value.trim().toLowerCase();
  return VALUE_EN[key] || value;
}

/** Löst pro Schema-Item die (value, note, severity) für die aktuelle
 *  Property auf — Basis-Keys aus den first-class Feldern, Custom-Keys
 *  aus `property.custom_status[key]`. */
export function readTileData(schemaItem, property) {
  const { key } = schemaItem;
  if (LOCKED_KEYS.has(key)) {
    const raw = property[key];
    // Temperatur mit °C-Suffix, wenn nur eine Zahl im Feld liegt.
    let value = raw != null && raw !== "" ? String(raw) : "";
    if (key === "temperature" && value && !value.includes("°")) value = `${value}°C`;
    return {
      value: value || "—",
      note: property[`${key}_note`] || null,
      severity: property[`${key}_severity`] || "ok",
    };
  }
  const entry = (property.custom_status || {})[key] || {};
  return {
    value: entry.value || "—",
    note: entry.note || null,
    severity: entry.severity || "ok",
  };
}

/**
 * Rendert genau die vom Admin ausgewählten Kacheln (max. 6) für den
 * Kunden. `schema` ist die aktuelle Master-Liste. `property.dashboard_tile_keys`
 * bestimmt, welche 6 wirklich angezeigt werden. Fehlt die Auswahl → erste
 * 6 aus dem Schema (Default für neue Kunden).
 */
export default function CustomerStatusPreview({ property, schema = [], testidPrefix = "" }) {
  const { lang } = useI18n();
  const online = useOnline();
  const de = lang !== "en";
  const DEFAULTS = de ? LOCKED_DEFAULT_ITEMS_DE : LOCKED_DEFAULT_ITEMS_EN;
  if (!property) return null;
  const tid = (base) => testidPrefix ? `${testidPrefix}-${base}` : base;

  const bySchema = new Map((schema || []).map((it) => [it.key, it]));
  const tiles = LOCKED_KEYS_ORDER.map(
    (key) => bySchema.get(key) || DEFAULTS[key]
  );

  return (
    <div>
      {online === false && (
        <div
          data-testid={tid("status-offline-badge")}
          className="flex items-center gap-1 text-[10px] tracking-[0.08em] uppercase text-navy/35 mb-2"
          title="Offline – lokal gespeicherte Daten"
        >
          <CloudOff className="w-3 h-3" strokeWidth={1.6} />
          <span>Lokal gespeichert</span>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3" data-testid={tid("status-grid")}>
        {tiles.map((item) => {
          const Icon = resolveIcon(item.icon);
          const { value, note, severity } = readTileData(item, property);
          const cleanLabel = translateLabel(item.label.replace(/\s*\(°C\)\s*$/, ""), de);
          return (
            <StatusCard
              key={item.key}
              icon={Icon}
              label={cleanLabel}
              value={translateValue(value, de)}
              note={note}
              severity={severity}
              testid={tid(`status-${item.key.replace(/_/g, "-")}`)}
            />
          );
        })}
      </div>
    </div>
  );
}
